import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";

import AppDialog from "components/common/AppDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import type { BotNote, BotNoteLink } from "features/adminUsers/adminUsers";

import { noteByline } from "./BotNoteView";

const PLACEHOLDER = [
  "How many at once: ...",
  "Speed: ... employees an hour",
  "Before you run: ...",
  "Options: ...",
  "Good to know: ...",
].join("\n");

/**
 * Edit one note (CAL-6162): a large text field for the points a manager needs
 * before a run, the pages it links, and who changed it last.
 */
export default function BotNoteEditDialog({
  open,
  title,
  note,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  /** The vendor or the bot the note belongs to. */
  title: string;
  note: BotNote | null | undefined;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  /** An empty text with no links removes the note. */
  onSave: (text: string, links: BotNoteLink[]) => Promise<boolean>;
}) {
  const [text, setText] = useState("");
  const [links, setLinks] = useState<BotNoteLink[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setText(note?.text ?? "");
    setLinks((note?.links ?? []).map((link) => ({ ...link })));
    setConfirmDelete(false);
  }, [open, note]);

  const exists = !!note && (!!note.text || (note.links ?? []).length > 0);
  const kept = links.filter((link) => link.label.trim() || link.url.trim());
  const byline = note ? noteByline(note) : "";
  const editLink = (index: number, field: keyof BotNoteLink, value: string) =>
    setLinks((rows) =>
      rows.map((row, at) => (at === index ? { ...row, [field]: value } : row)),
    );

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        showCloseIcon
        title={exists ? `Edit note - ${title}` : `Add note - ${title}`}
        actions={
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: "100%" }}
            alignItems="center"
          >
            {exists ? (
              <Button
                color="error"
                startIcon={<DeleteOutline />}
                disabled={saving}
                onClick={() => setConfirmDelete(true)}
              >
                Delete note
              </Button>
            ) : null}
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={() => void onSave(text, kept)}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Stack>
        }
      >
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Note"
            multiline
            minRows={10}
            maxRows={24}
            fullWidth
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={PLACEHOLDER}
            helperText="Short points a manager needs before running it. No logins, passwords or client data - link the page instead."
          />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
              Links
            </Typography>
            <Stack spacing={1}>
              {links.map((link, index) => (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  key={index}
                >
                  <TextField
                    size="small"
                    label="Title"
                    value={link.label}
                    onChange={(e) => editLink(index, "label", e.target.value)}
                    sx={{ flex: { sm: 1 } }}
                  />
                  <TextField
                    size="small"
                    sx={{ flex: { sm: 1 } }}
                    label="URL (https://...)"
                    value={link.url}
                    onChange={(e) => editLink(index, "url", e.target.value)}
                  />
                  <IconButton
                    aria-label="Remove link"
                    onClick={() =>
                      setLinks((rows) => rows.filter((_, at) => at !== index))
                    }
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() =>
                    setLinks((rows) => [...rows, { label: "", url: "" }])
                  }
                >
                  Add link
                </Button>
              </Box>
            </Stack>
          </Box>
          {byline ? (
            <Typography variant="caption" color="text.secondary">
              {byline}
            </Typography>
          ) : null}
        </Stack>
      </AppDialog>
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this note?"
        message={`The note of ${title} and its links are removed for everybody.`}
        confirmLabel="Delete"
        loading={saving}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          if (await onSave("", [])) setConfirmDelete(false);
        }}
      />
    </>
  );
}
